import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTaller3D } from '../../context/Taller3DContext';
import { useClientes } from '../../context/ClientesContext';
import { ParametrosPedido3D, LineaPedido3D, ExtraPedido3D } from '../../types';
import {
  calcularPedido3D, resumenTexto, formatearImporte, formatearPorcentaje, formatearDuracion,
  multiplicadorSugerido, parametrosIniciales, lineaNueva, nuevoIdLinea,
  IMPRESORAS_PRESET, MONEDAS_3D, TRAMOS_MULTIPLICADOR,
} from '../../utils/calculo3d';
import { Bloque, Campo, NumInput, Segmentado, claseInput } from '../../components/taller3d/campos';
import { LineaCard3D } from '../../components/taller3d/LineaCard3D';
import { Plus, Trash2, Save, Copy, Printer, AlertTriangle, Check, UserPlus } from 'lucide-react';
import { ModalCliente } from '../../components/clientes/ModalCliente';
import { copiarTexto } from '../../utils/plataforma';
import { useConfirmar } from '../../context/ConfirmarContext';

const BORRADOR_KEY = 'rzcentral.taller3d.borrador';

const COLORES_DESGLOSE = {
  filamento:    '#3E5C76',
  electricidad: '#C9963A',
  manoObra:     '#5E7D5A',
  maquina:      '#7A6A8F',
  extras:       '#9AA0A6',
};

export const Calculadora3D: React.FC = () => {
  const confirmar = useConfirmar();
  const {
    perfilesFilamento, perfilesImpresora, presupuestos3D,
    guardarPerfilFilamento, guardarPerfilImpresora, eliminarPerfilImpresora,
    guardarPresupuesto3D,
  } = useTaller3D();

  const { clientesActivos, clientePorId } = useClientes();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const idEdicion = searchParams.get('id') || undefined;
  const clientePreseleccionado = searchParams.get('cliente') || undefined;

  const [p, setP] = useState<ParametrosPedido3D>(() => {
    try {
      const guardado = localStorage.getItem(BORRADOR_KEY);
      if (guardado) {
        const parseado = JSON.parse(guardado);
        if (Array.isArray(parseado?.lineas) && parseado.lineas.length) {
          return { ...parametrosIniciales(), ...parseado };
        }
      }
    } catch { /* borrador ilegible: se ignora */ }
    return parametrosIniciales();
  });

  const [multTocado, setMultTocado] = useState(false);
  const [aviso, setAviso] = useState('');
  const [perfilPrnSel, setPerfilPrnSel] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [clienteId, setClienteId] = useState<string | undefined>(clientePreseleccionado);
  const [modalCliente, setModalCliente] = useState(false);

  const set = useCallback(<K extends keyof ParametrosPedido3D>(k: K, v: ParametrosPedido3D[K]) => {
    setP(prev => ({ ...prev, [k]: v }));
  }, []);

  const notificar = (m: string) => {
    setAviso(m);
    window.setTimeout(() => setAviso(a => (a === m ? '' : a)), 2400);
  };

  // Cargar un presupuesto guardado cuando se llega con ?id=
  useEffect(() => {
    if (!idEdicion) return;
    const pre = presupuestos3D.find(x => x.id === idEdicion);
    if (pre?.parametros?.lineas?.length) {
      setP({ ...parametrosIniciales(), ...pre.parametros });
      setClienteId(pre.clienteId);
      setMultTocado(true);
    }
  }, [idEdicion, presupuestos3D]);

  const r = useMemo(() => calcularPedido3D(p), [p]);
  const f = (v: number) => formatearImporte(v, p.moneda);

  // El desplegable de modelo refleja el consumo actual en vez de guardar aparte
  // qué se eligió: así nunca muestra una impresora que ya no corresponde.
  const modeloDetectado = IMPRESORAS_PRESET.find(i => i.vatios === p.vatios)?.nombre ?? '';

  // El multiplicador general sigue al total de unidades mientras no se toque
  useEffect(() => {
    if (multTocado || p.modo === 'precio') return;
    const sugerido = multiplicadorSugerido(r.unidadesTotales);
    setP(prev => (prev.multiplicador === sugerido ? prev : { ...prev, multiplicador: sugerido }));
  }, [r.unidadesTotales, p.modo, multTocado]);

  // Borrador local: no se pierde el trabajo al recargar
  useEffect(() => {
    const t = window.setTimeout(() => {
      try { localStorage.setItem(BORRADOR_KEY, JSON.stringify(p)); } catch { /* sin espacio */ }
    }, 300);
    return () => window.clearTimeout(t);
  }, [p]);

  const desglose = [
    { id: 'filamento',    nombre: 'Filamento',          valor: r.costeFilamento,    color: COLORES_DESGLOSE.filamento },
    { id: 'electricidad', nombre: 'Electricidad',       valor: r.costeElectricidad, color: COLORES_DESGLOSE.electricidad },
    { id: 'manoObra',     nombre: 'Tu tiempo',          valor: r.costeManoObra,     color: COLORES_DESGLOSE.manoObra },
    { id: 'maquina',      nombre: 'Máquina y desgaste', valor: r.costeMaquina,      color: COLORES_DESGLOSE.maquina },
    { id: 'extras',       nombre: 'Extras',             valor: r.costeExtras,       color: COLORES_DESGLOSE.extras },
  ];

  // ── Líneas ─────────────────────────────────────────────────────────────────
  const cambiarLinea = (indice: number, campo: keyof LineaPedido3D, valor: any) => {
    setP(prev => ({
      ...prev,
      lineas: prev.lineas.map((l, i) => {
        if (i !== indice) return l;
        if (valor === undefined) {
          const { [campo]: _quitado, ...resto } = l;
          return resto as LineaPedido3D;
        }
        return { ...l, [campo]: valor };
      }),
    }));
  };

  const anadirLinea = () =>
    setP(prev => ({ ...prev, lineas: [...prev.lineas, lineaNueva()] }));

  const duplicarLinea = (indice: number) =>
    setP(prev => {
      const copia: LineaPedido3D = {
        ...prev.lineas[indice],
        id: nuevoIdLinea(),
        nombre: `${prev.lineas[indice].nombre || `Artículo ${indice + 1}`} (copia)`,
        extras: prev.lineas[indice].extras.map(x => ({ ...x })),
      };
      const lineas = [...prev.lineas];
      lineas.splice(indice + 1, 0, copia);
      return { ...prev, lineas };
    });

  const eliminarLinea = (indice: number) =>
    setP(prev => (prev.lineas.length <= 1
      ? prev
      : { ...prev, lineas: prev.lineas.filter((_, i) => i !== indice) }));

  const guardarFilamentoDeLinea = async (indice: number) => {
    const l = p.lineas[indice];
    const nombre = window.prompt('Nombre del perfil de filamento', l.material);
    if (!nombre) return;
    await guardarPerfilFilamento({
      nombre,
      material: l.material,
      precioRollo: l.precioRollo,
      pesoRollo: l.pesoRollo,
      merma: l.merma,
      cambiosColor: l.cambiosColor,
      purgaPorCambio: l.purgaPorCambio,
    });
    notificar('Perfil de filamento guardado');
  };

  // ── Extras del pedido ──────────────────────────────────────────────────────
  const setExtra = (i: number, campo: keyof ExtraPedido3D, valor: any) =>
    setP(prev => ({
      ...prev,
      extras: prev.extras.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)),
    }));
  const quitarExtra = (i: number) =>
    setP(prev => ({ ...prev, extras: prev.extras.filter((_, j) => j !== i) }));
  const anadirExtra = () =>
    setP(prev => ({ ...prev, extras: [...prev.extras, { nombre: '', importe: 0 }] }));

  // ── Perfiles de impresora ──────────────────────────────────────────────────
  const cargarPerfilImpresora = (id: string) => {
    setPerfilPrnSel(id);
    const perfil = perfilesImpresora.find(x => x.id === id);
    if (!perfil) return;
    setP(prev => ({
      ...prev,
      vatios: perfil.vatios,
      precioKwh: perfil.precioKwh,
      costeImpresora: perfil.costeImpresora,
      vidaUtil: perfil.vidaUtil,
      mantenimiento: perfil.mantenimiento,
    }));
    notificar('Perfil de impresora cargado');
  };

  const guardarImpresora = async () => {
    const sugerido = perfilesImpresora.find(x => x.id === perfilPrnSel)?.nombre ?? 'Mi impresora';
    const nombre = window.prompt('Nombre del perfil de impresora', sugerido);
    if (!nombre) return;
    await guardarPerfilImpresora({
      nombre,
      vatios: p.vatios,
      precioKwh: p.precioKwh,
      costeImpresora: p.costeImpresora,
      vidaUtil: p.vidaUtil,
      mantenimiento: p.mantenimiento,
    });
    notificar('Perfil guardado');
  };

  const borrarImpresora = async () => {
    if (!perfilPrnSel) return notificar('Elige un perfil primero');
    if (!(await confirmar({ titulo: '¿Borrar este perfil de impresora?', accion: 'Borrar' }))) return;
    await eliminarPerfilImpresora(perfilPrnSel);
    setPerfilPrnSel('');
    notificar('Perfil borrado');
  };

  // ── Guardar presupuesto ────────────────────────────────────────────────────
  const enEdicion = Boolean(idEdicion && presupuestos3D.some(x => x.id === idEdicion));

  const guardarPresupuesto = async () => {
    const existente = idEdicion ? presupuestos3D.find(x => x.id === idEdicion) : undefined;
    const nombre = window.prompt(
      'Nombre del presupuesto',
      existente?.nombre || p.nombreTrabajo || 'Presupuesto',
    );
    if (!nombre) return;
    setGuardando(true);
    try {
      const id = await guardarPresupuesto3D({
        id: idEdicion, nombre, parametros: p, resultado: r, clienteId,
      });
      if (!idEdicion) setSearchParams({ id }, { replace: true });
      notificar(enEdicion ? 'Presupuesto actualizado' : 'Presupuesto guardado');
    } catch (e) {
      console.error(e);
      notificar('No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const nuevoPresupuesto = () => {
    setP(parametrosIniciales());
    setClienteId(undefined);
    setMultTocado(false);
    setSearchParams({}, { replace: true });
    notificar('Calculadora en blanco');
  };

  /**
   * El modo oscuro imprimiría fondos negros y gastaría media tinta, así que se
   * quita justo antes de imprimir y se repone al terminar. El temporizador es
   * la red por si el navegador no dispara 'afterprint'.
   */
  const imprimir = () => {
    const raiz = document.documentElement;
    if (!raiz.classList.contains('dark')) { window.print(); return; }

    raiz.classList.remove('dark');
    let repuesto = false;
    const reponer = () => {
      if (repuesto) return;
      repuesto = true;
      raiz.classList.add('dark');
      window.removeEventListener('afterprint', reponer);
    };
    window.addEventListener('afterprint', reponer);
    window.setTimeout(reponer, 3000);
    window.print();
  };

  const copiarResumen = async () => {
    const texto = resumenTexto(p, r);
    try {
      await copiarTexto(texto);
      notificar('Resumen copiado');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); notificar('Resumen copiado'); }
      catch { notificar('No se pudo copiar'); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">

      {/* Cabecera solo para el papel: un presupuesto, no una calculadora */}
      <div className="hidden print:block mb-4 pb-3 border-b border-gray-400">
        <h1 className="text-xl font-bold">{p.nombreTrabajo || 'Presupuesto de impresión 3D'}</h1>
        <p className="text-sm mt-1">
          {clienteId && `${clientePorId(clienteId)?.nombre ?? ''} · `}
          {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
          {` · ${r.unidadesTotales} ud${r.unidadesTotales === 1 ? '' : 's'}`}
        </p>
      </div>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Coste real · Precio de venta
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1">
            Calculadora de impresión 3D
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
            Un presupuesto es un pedido con uno o varios artículos. Cada artículo lleva su geometría y su
            filamento; la impresora, tu tarifa y los costes fijos son del pedido entero y se reparten entre todos.
          </p>
        </div>
        {enEdicion && (
          <button onClick={nuevoPresupuesto}
            className="px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-600
                       text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            Empezar uno nuevo
          </button>
        )}
      </header>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px] items-start">

        {/* ═══ Entradas ═══════════════════════════════════════════════════ */}
        <div className="print:hidden">

          <Bloque n="01" titulo="El pedido" subtitulo="Nombre y moneda" abiertoPorDefecto>
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Nombre del pedido">
                <input type="text" value={p.nombreTrabajo}
                  onChange={e => set('nombreTrabajo', e.target.value)}
                  placeholder="Encargo de Marta" className={claseInput} />
              </Campo>
              <Campo label="Moneda">
                <select value={p.moneda} onChange={e => set('moneda', e.target.value)} className={claseInput}>
                  {MONEDAS_3D.map(m => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
                </select>
              </Campo>
            </div>
            <div className="mt-3">
              <Campo label="Cliente"
                ayuda={clienteId
                  ? 'El presupuesto queda en su ficha, con su historial.'
                  : 'Opcional. Puedes asignarlo ahora o más adelante.'}>
                <div className="flex gap-2">
                  <select value={clienteId ?? ''}
                    onChange={e => setClienteId(e.target.value || undefined)}
                    className={claseInput}>
                    <option value="">— Sin cliente —</option>
                    {clientesActivos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    {/* Un cliente inactivo ya asignado debe seguir viéndose */}
                    {clienteId && !clientesActivos.some(c => c.id === clienteId) && (
                      <option value={clienteId}>
                        {clientePorId(clienteId)?.nombre ?? 'Cliente no encontrado'}
                      </option>
                    )}
                  </select>
                  <button type="button" onClick={() => setModalCliente(true)} title="Crear un cliente nuevo"
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                               text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition shrink-0">
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
              </Campo>
            </div>
          </Bloque>

          {/* ── Artículos ─────────────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-3 mt-5 mb-2 px-1">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
              Artículos
              <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                {p.lineas.length} línea{p.lineas.length === 1 ? '' : 's'} · {r.unidadesTotales} ud
                {r.unidadesTotales === 1 ? '' : 's'} · {r.platosTotales} plato{r.platosTotales === 1 ? '' : 's'}
              </span>
            </h2>
          </div>

          {p.lineas.map((l, i) => (
            <LineaCard3D
              key={l.id}
              linea={l}
              resultado={r.lineas.find(x => x.id === l.id)}
              indice={i}
              total={p.lineas.length}
              moneda={p.moneda}
              modo={p.modo}
              multiplicadorPedido={p.multiplicador}
              perfilesFilamento={perfilesFilamento}
              onChange={(campo, valor) => cambiarLinea(i, campo, valor)}
              onDuplicar={() => duplicarLinea(i)}
              onEliminar={() => eliminarLinea(i)}
              onGuardarPerfil={() => guardarFilamentoDeLinea(i)}
              abiertaPorDefecto={p.lineas.length === 1}
            />
          ))}

          <button type="button" onClick={anadirLinea}
            className="w-full mb-5 py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600
                       text-sm font-semibold text-gray-600 dark:text-gray-400
                       hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400 transition
                       inline-flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4" /> Añadir otro artículo
          </button>

          {/* ── Comunes al pedido ─────────────────────────────────────── */}
          <Bloque n="02" titulo="Máquina y electricidad" subtitulo="Común a todo el pedido">
            <div className="flex flex-wrap gap-2 items-center p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg mt-3 mb-3">
              <span className="w-full font-mono text-[10px] uppercase tracking-widest text-gray-400">
                Perfiles de impresora
              </span>
              <select value={perfilPrnSel} onChange={e => cargarPerfilImpresora(e.target.value)}
                className={`${claseInput} flex-1 min-w-[150px]`}>
                <option value="">— Cargar perfil —</option>
                {perfilesImpresora.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
              </select>
              <button type="button" onClick={guardarImpresora}
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition">
                Guardar actual
              </button>
              <button type="button" onClick={borrarImpresora}
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:border-red-400 hover:text-red-600 transition">
                Borrar
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo label="Modelo habitual"
                ayuda="Solo rellena el consumo medio. Si lo ajustas a mano, aquí pondrá «Personalizado».">
                <select value={modeloDetectado}
                  onChange={e => {
                    const modelo = IMPRESORAS_PRESET.find(i => i.nombre === e.target.value);
                    if (modelo) set('vatios', modelo.vatios);
                  }}
                  className={claseInput}>
                  <option value="">— Personalizado —</option>
                  {IMPRESORAS_PRESET.map(i => (
                    <option key={i.nombre} value={i.nombre}>{i.nombre} · {i.vatios} W</option>
                  ))}
                </select>
              </Campo>
              <Campo label="Consumo medio" ayuda="Media durante toda la impresión, no el pico de calentamiento.">
                <NumInput valor={p.vatios} onChange={v => set('vatios', v)} unidad="W" />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Precio de la electricidad"
                ayuda="España: 0,15–0,22 €/kWh. Tramo valle nocturno: ~0,07 €/kWh.">
                <NumInput valor={p.precioKwh} onChange={v => set('precioKwh', v)} unidad={`${p.moneda}/kWh`} step={0.001} />
              </Campo>
              <Campo label="Coste de la impresora" ayuda="Con extras: AMS, placas, enclosure…">
                <NumInput valor={p.costeImpresora} onChange={v => set('costeImpresora', v)} unidad={p.moneda} />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Vida útil estimada"
                ayuda="Horas de impresión hasta reemplazarla. Media: 3.000–6.000 h.">
                <NumInput valor={p.vidaUtil} onChange={v => set('vidaUtil', v)} unidad="h" min={1} step={100} />
              </Campo>
              <Campo label="Repuestos y mantenimiento"
                ayuda="Sobre la amortización: boquillas, correas, placas, ventiladores. Típico 10–20 %.">
                <NumInput valor={p.mantenimiento} onChange={v => set('mantenimiento', v)} unidad="%" />
              </Campo>
            </div>
          </Bloque>

          <Bloque n="03" titulo="Fallos" subtitulo="Lo que tiras y vuelves a imprimir">
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Tasa de fallo" ayuda="De cada 100 piezas, cuántas acaban en la basura.">
                <NumInput valor={p.tasaFallo} onChange={v => set('tasaFallo', v)} unidad="%" max={90} step={0.5} />
              </Campo>
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Cómo se aplica</p>
                <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-500">
                  Un fallo gasta plástico, horas de máquina, luz y desgaste, y además obliga a lanzar más
                  platos. Por eso el material, la electricidad, la amortización y el número de platos se
                  calculan sobre 1 ÷ (1 − tasa) piezas. El post-procesado no: solo lo haces sobre las piezas
                  buenas que llegas a entregar.
                </p>
              </div>
            </div>
          </Bloque>

          <Bloque n="04" titulo="Tu tiempo" subtitulo="Tarifa, diseño, preparación y manejo de platos">
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Tu tarifa por hora"
                ayuda="Mínimo razonable 10–15 €/h. Poner 0 aquí es regalar tu tiempo.">
                <NumInput valor={p.tarifaHora} onChange={v => set('tarifaHora', v)} unidad={`${p.moneda}/h`} step={0.5} />
              </Campo>
              <Campo label="Manejo de cada plato"
                ayuda="Retirar piezas, limpiar la placa y relanzar. Se cobra por cada plato de cada artículo.">
                <NumInput valor={p.minPorPlato} onChange={v => set('minPorPlato', v)} unidad="min" />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="Diseño y modelado" ayuda="Una vez por pedido, se reparte entre todas las líneas.">
                <NumInput valor={p.minDiseno} onChange={v => set('minDiseno', v)} unidad="min" step={5} />
              </Campo>
              <Campo label="Preparación" ayuda="Laminar y organizar el pedido. Una vez por pedido.">
                <NumInput valor={p.minPreparacion} onChange={v => set('minPreparacion', v)} unidad="min" step={5} />
              </Campo>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-3">
              El post-procesado de cada pieza se indica dentro de su artículo, porque no todos cuestan lo mismo de rematar.
            </p>
          </Bloque>

          <Bloque n="05" titulo="Costes del pedido" subtitulo="Envío, caja, seguro…">
            <div className="mt-3 space-y-2">
              {p.extras.map((x, i) => (
                <div key={i} className="grid gap-2 grid-cols-[1fr_130px_auto] items-center">
                  <input type="text" value={x.nombre} placeholder="Envío, caja, seguro…"
                    onChange={e => setExtra(i, 'nombre', e.target.value)} className={claseInput} />
                  <NumInput valor={x.importe} onChange={v => setExtra(i, 'importe', v)} unidad={p.moneda} step={0.01} />
                  <button type="button" onClick={() => quitarExtra(i)} title="Quitar concepto"
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-red-600 hover:border-red-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={anadirExtra}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg
                         border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              <Plus className="w-4 h-4" /> Añadir concepto
            </button>
            <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-3">
              Se paga una vez por pedido y se reparte entre las líneas a prorrata de su coste. Lo que sea por
              unidad (una anilla, pintura) va dentro del artículo correspondiente.
            </p>
          </Bloque>

          <Bloque n="06" titulo="Margen e impuestos" subtitulo="Cómo se fija el precio, IVA y redondeo">
            <div className="mt-3">
              <Campo label="Modo de cálculo">
                <Segmentado valor={p.modo} onChange={v => set('modo', v as ParametrosPedido3D['modo'])}
                  opciones={[{ valor: 'margen', label: 'Fijo un margen' }, { valor: 'precio', label: 'Fijo un precio' }]} />
              </Campo>
            </div>
            {p.modo === 'margen' ? (
              <div className="grid gap-3 sm:grid-cols-2 mt-3">
                <Campo label="Margen del pedido"
                  ayuda="Precio = coste × multiplicador. Se ajusta solo al total de unidades; cada artículo puede llevar el suyo.">
                  <NumInput valor={p.multiplicador} min={1} step={0.1}
                    onChange={v => { setMultTocado(true); set('multiplicador', v); }} unidad="×" />
                </Campo>
              </div>
            ) : (
              <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-3">
                En este modo el precio objetivo se indica dentro de cada artículo, porque cada uno tiene el suyo.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 mt-3">
              <Campo label="IVA aplicable">
                <select value={p.iva} onChange={e => set('iva', parseFloat(e.target.value))} className={claseInput}>
                  <option value={0}>Sin IVA</option>
                  <option value={4}>4 %</option>
                  <option value={10}>10 %</option>
                  <option value={21}>21 % (ES)</option>
                  <option value={16}>16 % (MX)</option>
                  <option value={19}>19 % (CO/CL)</option>
                </select>
              </Campo>
              <Campo label="Redondeo del precio">
                <select value={p.redondeo} onChange={e => set('redondeo', parseFloat(e.target.value))}
                  className={claseInput} disabled={p.modo === 'precio'}>
                  <option value={0}>Sin redondear</option>
                  <option value={0.5}>A 0,50</option>
                  <option value={1}>A 1</option>
                  <option value={0.95}>Acabado en ,95</option>
                </select>
              </Campo>
            </div>
          </Bloque>

          {/* Acciones */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-3">Presupuesto</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={guardarPresupuesto} disabled={guardando}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                           bg-orange-600 hover:bg-orange-700 text-white transition disabled:opacity-60">
                <Save className="w-4 h-4" />
                {enEdicion ? 'Actualizar presupuesto' : 'Guardar presupuesto'}
              </button>
              <button type="button" onClick={() => navigate('/taller3d/presupuestos')}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                           border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                           hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                Ver guardados ({presupuestos3D.length})
              </button>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-3">
              Los presupuestos y los perfiles se guardan en tu cuenta, así que los tienes en cualquier dispositivo.
              Lo que estás editando ahora se conserva también en este navegador aunque no lo guardes.
            </p>
          </div>
        </div>

        {/* ═══ Resultados ═════════════════════════════════════════════════ */}
        <aside className="lg:sticky lg:top-20 space-y-3 imprimible-compacto">

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 imprimible-bloque">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">
              A dónde va tu dinero · pedido entero
            </p>
            <div className="flex h-4 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 mb-3 imprimible-color">
              {desglose.map(d => (
                <div key={d.id} style={{
                  background: d.color,
                  width: `${r.costeTotal > 0 ? Math.max(0, d.valor / r.costeTotal * 100) : 20}%`,
                }} />
              ))}
            </div>
            <ul className="space-y-1.5 imprimible-color">
              {desglose.map(d => (
                <li key={d.id} className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                  <span className="flex-1 text-gray-600 dark:text-gray-400">{d.nombre}</span>
                  <span className="font-mono text-[11px] text-gray-400 w-9 text-right">
                    {r.costeTotal > 0 ? `${Math.round(d.valor / r.costeTotal * 100)}%` : ''}
                  </span>
                  <span className="font-mono tabular-nums text-gray-900 dark:text-white">{f(d.valor)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-baseline border-t border-gray-200 dark:border-gray-700 mt-3 pt-3">
              <span className="font-bold text-gray-900 dark:text-white">Coste del pedido</span>
              <span className="font-mono font-semibold text-lg tabular-nums text-gray-900 dark:text-white">
                {f(r.costeTotal)}
              </span>
            </div>
            {r.avisos.length > 0 && (
              <div className="mt-3 space-y-2">
                {r.avisos.slice(0, 2).map((a, i) => (
                  <div key={i} className="flex gap-2 items-start bg-orange-50 dark:bg-orange-900/20
                                          border border-orange-200 dark:border-orange-800
                                          text-orange-800 dark:text-orange-300 rounded-lg px-3 py-2 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {p.modo === 'margen' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 print:hidden">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">
                Margen según el tamaño del pedido
              </p>
              <div className="grid grid-cols-5 gap-1.5">
                {TRAMOS_MULTIPLICADOR.map(t => {
                  const activo = Math.abs(t.mult - p.multiplicador) < 0.001;
                  return (
                    <button key={t.mult} type="button"
                      onClick={() => { setMultTocado(true); set('multiplicador', t.mult); }}
                      aria-pressed={activo}
                      className={`flex flex-col items-center gap-0.5 py-2 rounded-lg border text-center transition ${
                        activo
                          ? 'bg-gray-900 dark:bg-orange-600 border-gray-900 dark:border-orange-600 text-white'
                          : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                      }`}>
                      <b className="font-mono text-sm">×{t.mult}</b>
                      <span className={`text-[9px] ${activo ? 'text-white/70' : 'text-gray-400'}`}>{t.etiqueta}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-3">
                Calculado sobre las {r.unidadesTotales} unidades del pedido. Más volumen es menos riesgo por pieza.
              </p>
            </div>
          )}

          <div className="rounded-xl bg-gray-900 dark:bg-black text-white p-5 imprimible-bloque imprimible-plano">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              Total del pedido{p.iva > 0 ? ' (con IVA)' : ''}
            </p>
            <p className="text-4xl font-bold text-orange-500 tabular-nums mt-2 leading-none">
              {f(r.precioTotalConIva)}
            </p>
            <p className="font-mono text-xs text-white/60 mt-1.5">
              {p.iva > 0 ? `${f(r.precioTotal)} sin IVA` : 'IVA no aplicado'}
            </p>

            {/* Desglose por línea */}
            <div className="mt-4 pt-4 border-t border-white/15 space-y-2">
              {r.lineas.map(l => (
                <div key={l.id} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-white/80 min-w-0">
                    {l.nombre || 'Sin nombre'}
                    <span className="text-white/40 text-xs"> ×{l.unidades}</span>
                  </span>
                  <span className="font-mono tabular-nums shrink-0">{f(l.precioTotalConIva)}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4 pt-4 border-t border-white/15">
              <div>
                <p className="text-[11px] text-white/50 mb-0.5">Coste total</p>
                <p className="font-mono text-sm tabular-nums break-words">{f(r.costeTotal)}</p>
              </div>
              <div>
                <p className="text-[11px] text-white/50 mb-0.5">Margen sobre el precio</p>
                <p className="font-mono text-sm tabular-nums">{formatearPorcentaje(r.margenSobrePrecio)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[11px] text-white/50 mb-0.5">Tu beneficio</p>
                <p className={`font-mono text-sm tabular-nums break-words ${
                  r.beneficioTotal < 0 ? 'text-red-400' : 'text-orange-500'
                }`}>{f(r.beneficioTotal)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 imprimible-bloque">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Realidad del pedido</p>
            {[
              ['Beneficio por hora de máquina', r.horasMaquina > 0 ? `${f(r.beneficioPorHoraMaquina)}/h` : '—'],
              ['Horas de impresora ocupada', formatearDuracion(r.horasMaquina)],
              ['Platos que hay que lanzar', `${r.platosTotales}`],
              ['Tu tiempo dedicado', formatearDuracion(r.horasPropias)],
              ['Filamento total (con purga y fallos)',
                `${r.gramosTotales.toLocaleString('es-ES', { maximumFractionDigits: 1 })} g (${f(r.costeFilamentoTotal)})`],
            ].map(([k, v]) => (
              <p key={k} className="flex justify-between gap-3 text-[13px] text-gray-600 dark:text-gray-400 py-1">
                <span>{k}</span>
                <b className="font-mono font-medium tabular-nums text-gray-900 dark:text-white text-right">{v}</b>
              </p>
            ))}
            <div className="flex flex-wrap gap-2 mt-4 print:hidden">
              <button type="button" onClick={copiarResumen}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg
                           bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 transition">
                <Copy className="w-4 h-4" /> Copiar resumen
              </button>
              <button type="button" onClick={imprimir}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg
                           border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300
                           hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                <Printer className="w-4 h-4" /> Imprimir / PDF
              </button>
            </div>
          </div>
        </aside>
      </div>

      <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-6 max-w-3xl print:hidden">
        Las estimaciones son solo eso: contrasta el consumo real de tu impresora con un medidor de enchufe y
        revisa tu tasa de fallo cada pocas semanas.
      </p>

      {modalCliente && (
        <ModalCliente
          onClose={() => setModalCliente(false)}
          onGuardado={id => { setClienteId(id); notificar('Cliente creado y asignado'); }}
        />
      )}

      {aviso && (
        <div data-sin-imprimir
          className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-8 z-50
                        flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-900 text-white text-sm font-semibold shadow-lg">
          <Check className="w-4 h-4" /> {aviso}
        </div>
      )}
    </div>
  );
};
